import React from 'react';
import { Tag, Button, Tooltip } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import styles from './Audit.module.css';

dayjs.extend(utc);
dayjs.extend(timezone);

export const ACTION_COLORS: Record<string, string> = {
  INSERT: '#52c41a',
  CREATE: '#52c41a',
  UPDATE: '#0b71ff',
  CREATE_REVISION: '#13c2c2',
  APPROVE: '#722ed1',
  EMAIL_SENT: '#1890ff',
  DELETE: '#ff4d4f',
  DOWNLOAD: '#8c8c8c',
  EXPORT: '#8c8c8c',
  'REVISED BILING FORM': '#8c8c8c',
};

export const formatDate = (iso: string) => {
  const d = dayjs.utc(iso).tz('America/Halifax');
  return (
    <span>
      {d.format('MMM D, YYYY, HH:mm')}
      <br />
      <span style={{ color: '#888', fontSize: 11 }}>AT</span>
    </span>
  );
};

export const JsonPanel = ({ label, data }: { label: 'BEFORE' | 'AFTER'; data: any }) => (
  <div className={styles.jsonPanel}>
    <div className={styles.jsonPanelLabel}>{label}</div>
    <div className={styles.jsonPanelBody}>
      {data === null ? (
        <span className={styles.nullText}>null</span>
      ) : (
        <pre className={styles.jsonPre}>{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  </div>
);

export const ExpandedRow = ({ record }: { record: any }) => (
  <div className={styles.expandedRow}>
    <JsonPanel label="BEFORE" data={record.before} />
    <JsonPanel label="AFTER" data={record.after} />
  </div>
);

export function buildAuditColumns(expandedKeys: Set<string>, onToggle: (id: string) => void) {
  return [
    {
      key: 'expand',
      width: 20,
      render: (_: any, record: any) => {
        const canExpand = record.before !== null || record.after !== null;
        if (!canExpand) return null;
        const expanded = expandedKeys.has(record.id);
        return (
          <Button
            size="small"
            icon={expanded ? <MinusOutlined /> : <PlusOutlined />}
            onClick={() => onToggle(record.id)}
            style={{ height: 15, minWidth: 5, fontSize: 8 }}
          />
        );
      },
    },
    {
      title: 'Occurred At',
      dataIndex: 'occurredAt',
      key: 'occurredAt',
      width: 180,
      render: (v: string) => formatDate(v),
    },
    {
      title: 'Actor Name',
      dataIndex: 'actorName',
      key: 'actorName',
      width: 160,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 90,
      render: (v: string) => (
        <Tag
          style={{
            backgroundColor: `${ACTION_COLORS[v] ?? '#8c8c8c'}20`,
            color: ACTION_COLORS[v] ?? '#8c8c8c',
            fontWeight: 400,
            fontSize: 12,
            padding: '2px 6px',
            borderRadius: 6,
          }}
        >
          {v}
        </Tag>
      ),
    },
    {
      title: 'Table Name',
      dataIndex: 'tableName',
      key: 'tableName',
      width: 200,
    },
    {
      title: 'Primary Key',
      dataIndex: 'pkName',
      key: 'pkName',
      width: 110,
    },
    {
      title: 'Value',
      dataIndex: 'pkValue',
      key: 'pkValue',
      width: 80,
    },
    {
      title: 'Changed Columns',
      dataIndex: 'changedCols',
      key: 'changedCols',
      render: (cols: string[]) =>
        cols?.length ? (
          <Tooltip title={cols.join(', ')}>
            <span style={{ color: '#555', fontSize: 13 }}>{cols.join(', ')}</span>
          </Tooltip>
        ) : (
          '—'
        ),
    },
  ];
}
